import React from 'react';

import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { fmtAud, fmtInt, fmtPct } from '../PriceParrotChainParityDashboard/utils';

import type { ChainRollupOverview } from './utils';

interface Props {
  overview: ChainRollupOverview;
}

const TILE_SX = {
  flex: 1,
  minWidth: 160,
  p: 2,
  borderRadius: 2,
  bgcolor: 'grey.50',
  border: 1,
  borderColor: 'divider',
} as const;

export default function RollupSummaryTiles({ overview }: Props) {
  const { rollup } = overview;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={TILE_SX}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Bookings</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{fmtInt(rollup.bookings_count)}</Typography>
        </Box>
        <Box sx={TILE_SX}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Margin AR</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{fmtAud(rollup.margin_ar_aud)}</Typography>
        </Box>
        <Box sx={TILE_SX}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Avg margin / booking</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{fmtAud(rollup.avg_margin_per_booking_aud)}</Typography>
        </Box>
        <Box sx={TILE_SX}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>LPC / LPP views</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            {fmtInt(rollup.lpc_views)} / {fmtInt(rollup.lpp_views)}
          </Typography>
        </Box>
        <Box sx={TILE_SX}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>% LPC vs % LPP</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            {fmtPct(overview.pctLpc)} / {fmtPct(overview.pctLpp)}
          </Typography>
        </Box>
      </Stack>

      {overview.suppliers.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Chain-wide supplier breakdown</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>SUPPLIER</TableCell>
                  <TableCell sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>CHANNEL</TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                    BOOKINGS
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                    % OF LPC
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                    MARGIN AR
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
                    AVG MARGIN/BOOKING
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overview.suppliers.map((row) => (
                  <TableRow key={`${row.channel}-${row.supplier}`}>
                    <TableCell sx={{ fontSize: 13 }}>{row.supplier}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{row.channel}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {fmtInt(row.bookingsCount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {fmtPct(row.pctOfChainLpc)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {fmtAud(row.marginArAud)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>
                      {fmtAud(row.avgMarginPerBookingAud)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
